import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { DESTINATIONS } from "@/data/content";

export default function DestinationGrid() {
  const [lead, ...rest] = DESTINATIONS.slice(0, 5);
  return (
    <section data-testid="destinations-preview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <Eyebrow>Destinations</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy max-w-xl leading-tight">
            Plan your next escape
          </h2>
          <p className="mt-4 text-base text-charcoal/75 max-w-xl leading-relaxed">
            Destination guides that balance inspiration with the details that matter: where to stay, what to do,
            when to go, how to get around, and what to book ahead.
          </p>
        </div>
        <Link
          to="/destinations"
          data-testid="destinations-view-all"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors duration-300 shrink-0"
        >
          All destination guides
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        <Reveal className="md:col-span-2 md:row-span-2">
          <Link
            to="/destinations"
            data-testid={`destination-card-${lead.slug}`}
            className="group relative block h-full min-h-[420px] rounded-3xl overflow-hidden"
          >
            <img
              src={lead.image}
              alt={lead.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">
                {lead.city} — {lead.region}
              </p>
              <h3 className="mt-3 font-serif text-2xl sm:text-4xl text-ivory leading-tight max-w-md">{lead.title}</h3>
              <p className="mt-3 text-sm text-ivory/75 max-w-md leading-relaxed">{lead.blurb}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] uppercase text-gold">
                Read the guide
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </Link>
        </Reveal>
        {rest.map((d, i) => (
          <Reveal key={d.slug} delay={0.1 + i * 0.08}>
            <Link
              to="/destinations"
              data-testid={`destination-card-${d.slug}`}
              className="group relative block h-[200px] md:h-[calc(50%-10px)] min-h-[200px] rounded-3xl overflow-hidden mb-5 last:mb-0"
            >
              <img
                src={d.image}
                alt={d.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">{d.city}</p>
                <h3 className="mt-1.5 font-serif text-lg sm:text-xl text-ivory leading-snug">{d.title}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
